import React, { useState, useEffect, useCallback } from 'react';
import './ProgramList.css';

import { enrollUserInProgram, checkEnrollmentEligibility, updateEnrollmentActivity } from '../utils/enrollmentUtils';
import config from '../config';

const { API_URL } = config;

const ProgramList = ({ user, onMessage, userEnrollments = [], enrollmentsLoading = false, onEnrollmentUpdate, onNewEnrollment, onStartAssessment }) => {
  // State management
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null); // Track which program is being enrolled
  const [prerequisitesStatus, setPrerequisitesStatus] = useState({}); // Track prerequisites for each program
  const [prerequisitesLoading, setPrerequisitesLoading] = useState(true); // Track prerequisites loading state
  const [completedAssessments, setCompletedAssessments] = useState({}); // Track which assessments are completed

  // Load programs from API
  const loadPrograms = useCallback(async () => {
    try {
      // Demo mode - provide demo programs
      if (user?.token === 'demo-token-for-local-testing') {
        console.log('Demo mode: Using demo programs');
        const demoPrograms = [
          {
            name: 'Cloud Operator Demo',
            title: 'Cloud Operator Certification Demo',
            description: 'Demonstrate your cloud operations and DevOps skills with this comprehensive assessment.',
            assessment_name: 'Cloud Operator Demo',
            status: 'active',
            requirements: []
          },
          {
            name: 'DevOps Fundamentals Demo',
            title: 'DevOps Fundamentals Demo',
            description: 'Test your understanding of DevOps practices, CI/CD, and automation principles.',
            assessment_name: 'DevOps Fundamentals Demo',
            status: 'active',
            requirements: []
          },
          {
            name: 'AWS Basics Demo',
            title: 'AWS Basics Demo',
            description: 'Evaluate your knowledge of core AWS services and cloud architecture concepts.',
            assessment_name: 'AWS Basics Demo',
            status: 'active',
            requirements: []
          }
        ];
        setPrograms(demoPrograms);
        return;
      }

      const response = await fetch(`${API_URL}/admin/programs`);
      if (!response.ok) {
        if (response.status === 404) {
          setPrograms([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      
      const programsData = await response.json();
      // Filter only active programs for user enrollment
      const activePrograms = programsData.filter(program => program.status === 'active');
      setPrograms(activePrograms || []);
    } catch (error) {
      console.error('Error loading programs:', error);
      onMessage({ text: 'Error loading programs: ' + error.message, type: 'error' });
      setPrograms([]);
    }
  }, [onMessage, user?.token]);

  // Check prerequisites for enrolled programs
  const checkPrerequisites = useCallback(async () => {
    setPrerequisitesLoading(true);
    
    if (!user?.email || user?.token === 'demo-token-for-local-testing') {
      // Demo mode - all prerequisites met
      const demoStatus = {};
      userEnrollments.forEach(enrollment => {
        demoStatus[enrollment.program_name] = { met: true, verified: true };
      });
      setPrerequisitesStatus(demoStatus);
      setPrerequisitesLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/enrollments/prerequisites?user_email=${encodeURIComponent(user.email)}`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPrerequisitesStatus(data.prerequisites || {});
      } else {
        console.error('Failed to check prerequisites:', response.status);
        // Fail-closed: deny access if API fails (secure by default)
        const deniedStatus = {};
        userEnrollments.forEach(enrollment => {
          deniedStatus[enrollment.program_name] = { met: false, verified: false, error: true };
        });
        setPrerequisitesStatus(deniedStatus);
        onMessage({ 
          text: 'Unable to verify prerequisites. Please try again or contact support if the issue persists.', 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error checking prerequisites:', error);
      // Fail-closed: deny access if API fails (secure by default)
      const deniedStatus = {};
      userEnrollments.forEach(enrollment => {
        deniedStatus[enrollment.program_name] = { met: false, verified: false, error: true };
      });
      setPrerequisitesStatus(deniedStatus);
      onMessage({ 
        text: 'Unable to verify prerequisites. Please check your connection and try again.', 
        type: 'error' 
      });
    } finally {
      setPrerequisitesLoading(false);
    }
  }, [user?.email, user?.token, userEnrollments, onMessage]);

  // Check which assessments have been completed by querying attempts
  const checkCompletedAssessments = useCallback(async () => {
    if (!user?.email || user?.token === 'demo-token-for-local-testing') {
      setCompletedAssessments({});
      return;
    }

    try {
      // Add timestamp to bust browser cache
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_URL}/admin/user-attempts?user_email=${encodeURIComponent(user.email)}&_t=${timestamp}`, {
        headers: {
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const attempts = await response.json();
        // Build a map of assessment_name -> attempt details (including passed status)
        const completedMap = {};
        attempts.forEach(attempt => {
          if (attempt.assessment_name) {
            completedMap[attempt.assessment_name] = {
              completed: true,
              passed: attempt.passed,
              score: attempt.score
            };
          }
        });
        setCompletedAssessments(completedMap);
      } else {
        console.error('Failed to check completed assessments:', response.status);
        // Don't clear existing state on error - keep what we have
      }
    } catch (error) {
      console.error('Error checking completed assessments:', error);
      // Don't clear existing state on error - keep what we have
    }
  }, [user?.email, user?.token]);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await loadPrograms();
        // Load prerequisites status for enrolled programs
        if (user?.email && userEnrollments.length > 0) {
          await checkPrerequisites();
        }
        // Check which assessments have been completed
        if (user?.email) {
          await checkCompletedAssessments();
        }
      } finally {
        setLoading(false);
      }
    };

    loadData().catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPrograms, user?.email, userEnrollments, checkCompletedAssessments]);

  // Update activity for active enrollments when enrollments change
  useEffect(() => {
    if (user?.email && userEnrollments.length > 0) {
      const activeEnrollments = userEnrollments.filter(e => e.status === 'active');
      activeEnrollments.forEach(enrollment => {
        updateEnrollmentActivity(user.email, enrollment.program_name, user.token)
          .catch(error => console.log('Activity update failed:', error.message));
      });
    }
  }, [user?.email, user?.token, userEnrollments]);

  // Handle program enrollment
  const handleEnrollment = async (program) => {
    if (!user?.email) {
      onMessage({ text: 'User authentication required for enrollment', type: 'error' });
      return;
    }

    // Check eligibility
    const eligibility = checkEnrollmentEligibility(user, program, userEnrollments);
    if (!eligibility.eligible) {
      onMessage({ text: eligibility.reason, type: 'info' });
      return;
    }

    setEnrolling(program.name);

    try {
      // Demo mode - simulate enrollment
      if (user?.token === 'demo-token-for-local-testing') {
        // Add new enrollment in demo mode
        if (onNewEnrollment) {
          onNewEnrollment(user.email, program.name);
        }
        onMessage({ 
          text: `Demo: Successfully enrolled in ${program.title || program.name}! In production, this would create a real enrollment.`, 
          type: 'success' 
        });
      } else {
        // Real enrollment
        const result = await enrollUserInProgram(user.email, program.name, user.token);
        
        if (result.success) {
          // Reload enrollments to get updated data
          if (onEnrollmentUpdate) {
            await onEnrollmentUpdate();
          }
          onMessage({ 
            text: `Successfully enrolled in ${program.title || program.name}!`, 
            type: 'success' 
          });
        } else {
          onMessage({ text: result.message, type: 'error' });
        }
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      onMessage({ text: 'Enrollment failed: ' + error.message, type: 'error' });
    } finally {
      setEnrolling(null);
    }
  };

  // Get enrollment status for a program
  const getEnrollmentStatus = (programName) => {
    const enrollment = userEnrollments.find(e => e.program_name === programName);
    return enrollment ? enrollment.status : null;
  };

  // Check if assessment is completed for a program
  const isAssessmentCompleted = (programName) => {
    const program = programs.find(p => p.name === programName);
    if (!program) return false;
    
    // Only check from attempts API (most reliable and has pass/fail info)
    // Don't use enrollment.progress.completed_assessments as it may have old data from before pass/fail check
    if (completedAssessments[program.assessment_name]?.completed) {
      return true;
    }
    
    return false;
  };

  // Check if program has requirements
  const hasRequirements = (program) => {
    return program.requirements && program.requirements.length > 0;
  };

  if (loading) {
    return (
      <div className="program-list">
        <div className="program-list-header">
          <h2>Available Programs</h2>
        </div>
        <div className="loading">Loading programs...</div>
      </div>
    );
  }

  return (
    <div className="program-list">
      <div className="program-list-header">
        <h2>Available Programs</h2>
        <p className="program-list-description">
          Enroll in certification programs to access assessments and track your progress.
        </p>
      </div>

      {programs.length === 0 ? (
        <div className="no-programs">
          <h3>No Programs Available</h3>
          <p>There are currently no active programs available for enrollment.</p>
          <p>Please check back later or contact your administrator.</p>
        </div>
      ) : (
        <div className="programs-grid">
          {programs.map((program) => {
            const enrollmentStatus = getEnrollmentStatus(program.name);
            const isEnrolled = enrollmentStatus === 'active';
            const assessmentCompleted = isAssessmentCompleted(program.name);
            const isEnrolling = enrolling === program.name;
            
            // Determine detailed status
            const prereqStatus = prerequisitesStatus[program.name];
            let detailedStatus = 'Unenrolled';
            let statusClass = 'unenrolled';
            
            if (assessmentCompleted) {
              const attemptDetails = completedAssessments[program.assessment_name];
              const passed = attemptDetails?.passed;
              
              if (passed) {
                detailedStatus = 'Completed';
                statusClass = 'completed';
              } else {
                detailedStatus = 'Not Passed';
                statusClass = 'not-passed';
              }
            } else if (isEnrolled) {
              // Only surface certificate-related statuses for programs that
              // actually require the AWS Cloud Practitioner certificate
              // (declared via the program's requirements). Programs without it
              // (e.g. Cloud SysAdmin) just show "Enrolled".
              const requiresCertificate = (program.requirements || []).some(
                req => String(req).toLowerCase().includes('cloud practitioner')
              );

              if (requiresCertificate && prereqStatus?.verified) {
                detailedStatus = 'AWS Certified';
                statusClass = 'certified';
              } else if (requiresCertificate && prereqStatus?.met) {
                detailedStatus = 'Certificate Pending';
                statusClass = 'pending';
              } else {
                detailedStatus = 'Enrolled';
                statusClass = 'enrolled';
              }
            }

            return (
              <div key={program.name} className={`program-card ${statusClass}`}>
                <div className="program-card-header">
                  <h3 className="program-title">{program.title || program.name}</h3>
                  <span className={`status-badge ${statusClass}`}>{detailedStatus}</span>
                </div>

                <div className="program-card-body">
                  <p className="program-description">{program.description}</p>
                  
                  <div className="program-details">
                    <div className="program-detail">
                      <strong>Assessment:</strong> {program.assessment_name}
                    </div>
                    
                    {hasRequirements(program) && (
                      <div className="program-detail">
                        <strong>Program Pathway:</strong>
                        <ul className="requirements-list">
                          {program.requirements.map((requirement, index) => (
                            <li key={index}>{requirement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="program-card-footer">
                  {isEnrolled ? (
                    <div className="enrolled-actions">
                      <span className="enrollment-status">
                        Enrolled on {(() => {
                          try {
                            const enrollment = userEnrollments.find(e => e.program_name === program.name);
                            const enrollmentDate = enrollment?.enrollment_date;
                            if (!enrollmentDate) return 'Unknown date';
                            
                            const date = new Date(enrollmentDate);
                            if (isNaN(date.getTime())) return 'Invalid date';
                            
                            return date.toLocaleDateString();
                          } catch (error) {
                            console.error('Error formatting enrollment date:', error);
                            return 'Unknown date';
                          }
                        })()}
                      </span>
                      {!assessmentCompleted && (() => {
                        const prereqStatus = prerequisitesStatus[program.name];
                        const canTakeAssessment = prereqStatus?.met && prereqStatus?.verified;
                        const hasError = prereqStatus?.error;
                        const isLoading = prerequisitesLoading;
                        
                        return (
                          <button 
                            className="btn btn-primary take-assessment-button"
                            onClick={() => onStartAssessment && onStartAssessment(program.assessment_name, program.name)}
                            disabled={!canTakeAssessment || isLoading}
                            title={
                              isLoading ? 'Checking prerequisites...' :
                              hasError ? 'Error checking prerequisites - please refresh' :
                              !canTakeAssessment ? 'Prerequisites not met or not verified by admin' : 
                              'Start assessment'
                            }
                          >
                            {isLoading ? '⏳ Checking...' :
                             hasError ? '⚠️ Error - Refresh Page' :
                             canTakeAssessment ? 'Take Assessment' : 
                             '🔒 Prerequisites Required'}
                          </button>
                        );
                      })()}
                      {!assessmentCompleted && !prerequisitesStatus[program.name]?.verified && prerequisitesStatus[program.name]?.met === false && (
                        <p className="prerequisites-message">
                          ⚠️ Please provide your AWS Cloud Practitioner certificate to Cloud Team - EnproCloudServices@enpro.com - for validation and next steps
                        </p>
                      )}
                      {prerequisitesStatus[program.name]?.met && !prerequisitesStatus[program.name]?.verified && (
                        <p className="prerequisites-message">
                          ⏳ Certificate uploaded - waiting for admin verification
                        </p>
                      )}
                      {assessmentCompleted && (() => {
                        const attemptDetails = completedAssessments[program.assessment_name];
                        const passed = attemptDetails?.passed;
                        
                        if (passed) {
                          return <span className="enrollment-status">✅ Assessment completed successfully!</span>;
                        } else {
                          return (
                            <div>
                              <span className="enrollment-status">❌ Assessment not passed</span>
                              <p className="prerequisites-message">
                                To retake this assessment, please contact Cloud Team at EnproCloudServices@enpro.com
                              </p>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  ) : assessmentCompleted ? (
                    <div className="completed-actions">
                      {(() => {
                        const attemptDetails = completedAssessments[program.assessment_name];
                        const passed = attemptDetails?.passed;
                        
                        if (passed) {
                          return <span className="enrollment-status">✅ Assessment completed successfully!</span>;
                        } else {
                          return (
                            <div>
                              <span className="enrollment-status">❌ Assessment not passed</span>
                              <p className="prerequisites-message">
                                To retake this assessment, please contact Cloud Team at EnproCloudServices@enpro.com
                              </p>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  ) : (
                    <button 
                      className="btn btn-primary enroll-button"
                      onClick={() => handleEnrollment(program)}
                      disabled={isEnrolling}
                    >
                      {isEnrolling ? 'Enrolling...' : 'Enroll Now'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProgramList;